/* eslint-disable react/prop-types */
import {
  Button,
  Card,
  Modal,
  Space,
  Switch,
  Table,
  Typography,
  Tag,
  Tooltip,
  Avatar,
  Row,
  Col,
  Empty,
  Alert,
} from 'antd';
import React, { useEffect, useState } from 'react';
import './TableUsers.scss';
import useTenantFlags from 'hooks/useTenantFlags';
import BookEditForm from './BookEditForm';
import {
  BookOutlined,
  EditFilled,
  StopFilled,
  UserOutlined,
  TeamOutlined,
  MailOutlined,
  IdcardOutlined,
  UserAddOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilterOutlined,
  KeyOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

function TableUsers(props) {
  const {
    usersActive,
    usersInactive,
    openAddUser,
    openEditUser,
    openChangePassword,
    tiposGestionables,
    toggleActiveUser,
    loading,
    error,
  } = props;

  const { readOnly } = useTenantFlags();
  const [users, setUsers] = useState([]);
  const [showActiveUsers, setShowActiveUsers] = useState(true);
  const [modalContent, setModalContent] = useState(null);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [modalTitle, setModalTitle] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  useEffect(() => {
    if (showActiveUsers) {
      setUsers(usersActive);
    } else {
      setUsers(usersInactive);
    }
  }, [showActiveUsers, usersInactive, usersActive]);

  const editSpecialistBook = (user) => {
    setIsVisibleModal(true);
    setModalTitle('Información del especialista');
    setModalContent(<BookEditForm user={user} />);
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'especialista':
        return 'blue';
      case 'administrador':
        return 'green';
      case 'admin':
        return 'purple';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'especialista':
        return <UserOutlined />;
      case 'administrador':
        return <TeamOutlined />;
      case 'admin':
        return <UserOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
  };

  const clearAll = () => {
    setFilteredInfo({});
    setSortedInfo({});
  };

  const columns = [
    {
      title: (
        <span className="flex items-center">
          <IdcardOutlined className="mr-1" />
          RUT
        </span>
      ),
      dataIndex: 'rut',
      key: 'rut',
      width: 120,
      render: (rut) => (
        <Text code className="text-sm">
          {rut}
        </Text>
      ),
    },
    {
      title: (
        <span className="flex items-center">
          <UserOutlined className="mr-1" />
          Usuario
        </span>
      ),
      key: 'user',
      render: (_, record) => (
        <div className="flex items-center">
          <Avatar
            size={40}
            icon={<UserOutlined />}
            className="mr-3"
            style={{
              backgroundColor: record.active ? '#52c41a' : '#ff4d4f',
            }}
          />
          <div>
            <div className="font-medium capitalize">{record.fullname}</div>
            <div className="text-xs text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: (
        <span className="flex items-center">
          <TeamOutlined className="mr-1" />
          Cargo
        </span>
      ),
      dataIndex: 'type',
      key: 'type',
      width: 140,
      filters: [
        {
          text: 'Ejecutivo',
          value: 'ejecutivo',
        },
        {
          text: 'Especialista',
          value: 'especialista',
        },
      ],
      filteredValue: filteredInfo.type || null,
      onFilter: (value, record) => record.type === value,
      render: (type) => (
        <Tag color={getTypeColor(type)} icon={getTypeIcon(type)} className="capitalize">
          {type}
        </Tag>
      ),
    },
    {
      title: (
        <span className="flex items-center">
          <MailOutlined className="mr-1" />
          Email
        </span>
      ),
      dataIndex: 'email',
      key: 'email',
      render: (email) => <Text className="lowercase text-sm">{email}</Text>,
    },
    {
      title: 'Estado',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag
          color={record.active ? 'green' : 'red'}
          icon={record.active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {record.active ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      render: (data) => (
        <Space size="small">
          {!readOnly && (
            <Tooltip title={showActiveUsers ? 'Desactivar usuario' : 'Activar usuario'}>
              <Button
                type={showActiveUsers ? 'default' : 'primary'}
                danger={showActiveUsers}
                onClick={() => toggleActiveUser(data)}
                icon={showActiveUsers ? <StopFilled /> : <CheckCircleOutlined />}
                size="small"
                className="rounded-md"
              />
            </Tooltip>
          )}

          {!readOnly && (
            <Tooltip title="Editar usuario">
              <Button
                onClick={() => openEditUser(data)}
                type="default"
                icon={<EditFilled />}
                size="small"
                className="rounded-md"
              />
            </Tooltip>
          )}

          {/* Acción aparte de "Editar" a propósito: cambiar una contraseña deja al
              usuario afuera de su cuenta, no es un campo más del formulario. Solo se
              ofrece sobre los roles que el actor gestiona (`gestionable` de
              GET /user/roles); el candado real es del backend. */}
          {!readOnly && openChangePassword && tiposGestionables?.has(data.type) && (
            <Tooltip title="Cambiar contraseña">
              <Button
                onClick={() => openChangePassword(data)}
                type="default"
                icon={<KeyOutlined />}
                size="small"
                className="rounded-md"
              />
            </Tooltip>
          )}

          <Tooltip title="Ver información del especialista">
            <Button
              className="rounded-md"
              type="default"
              icon={<BookOutlined />}
              onClick={() => editSpecialistBook(data)}
              disabled={data.type?.toLowerCase() !== 'especialista'}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];
  return (
    <div className="space-y-6">
      {/* Header mejorado */}
      <Card className=" shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="text-center lg:text-left">
            <Title
              level={2}
              className="text-white flex items-center justify-center lg:justify-start mb-2"
            >
              <TeamOutlined className="mr-3 text-3xl" />
              Gestión de Usuarios
            </Title>
            <Text className="text-white opacity-90 text-lg">
              Administra usuarios del sistema y sus permisos
            </Text>
          </div>

          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Switch de estado */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <EyeOutlined className="text-white" />
                <Text className="text-white font-medium">
                  {showActiveUsers ? 'Usuarios Activos' : 'Usuarios Inactivos'}
                </Text>
              </div>
              <Switch
                checked={showActiveUsers}
                onChange={() => setShowActiveUsers(!showActiveUsers)}
                checkedChildren={<EyeOutlined />}
                unCheckedChildren={<EyeInvisibleOutlined />}
                size="default"
              />
            </div>

            {/* Botones de acción */}
            <div className="flex items-center space-x-2">
              <Button
                onClick={clearAll}
                size="large"
                icon={<FilterOutlined />}
                className="shadow-md"
              >
                Limpiar Filtros
              </Button>
              {!readOnly && (
                <Button
                  onClick={openAddUser}
                  type="primary"
                  size="large"
                  icon={<UserAddOutlined />}
                  className="shadow-md"
                >
                  Agregar Usuario
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Estadísticas rápidas */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card className="text-center shadow-md">
            <div className="flex items-center justify-center mb-2">
              <CheckCircleOutlined className="text-2xl text-green-500 mr-2" />
              <Text strong className="text-lg">
                Activos
              </Text>
            </div>
            <Text className="text-2xl font-bold text-green-600">{usersActive?.length || 0}</Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="text-center shadow-md">
            <div className="flex items-center justify-center mb-2">
              <CloseCircleOutlined className="text-2xl text-red-500 mr-2" />
              <Text strong className="text-lg">
                Inactivos
              </Text>
            </div>
            <Text className="text-2xl font-bold text-red-600">{usersInactive?.length || 0}</Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="text-center shadow-md">
            <div className="flex items-center justify-center mb-2">
              <TeamOutlined className="text-2xl text-blue-500 mr-2" />
              <Text strong className="text-lg">
                Total
              </Text>
            </div>
            <Text className="text-2xl font-bold text-blue-600">
              {(usersActive?.length || 0) + (usersInactive?.length || 0)}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Tabla de usuarios */}
      <Card className="shadow-md">
        {error ? (
          <Alert
            message="Error al cargar usuarios"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                Reintentar
              </Button>
            }
          />
        ) : (
          <Table
            rowKey="rut"
            dataSource={users}
            columns={columns}
            loading={loading}
            onChange={handleChange}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} usuarios`,
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div>
                      <Text type="secondary" className="block mb-2">
                        No hay usuarios {showActiveUsers ? 'activos' : 'inactivos'}
                      </Text>
                      {showActiveUsers && !readOnly && (
                        <Button type="primary" icon={<UserAddOutlined />} onClick={openAddUser}>
                          Agregar Primer Usuario
                        </Button>
                      )}
                    </div>
                  }
                />
              ),
            }}
            scroll={{ x: 800 }}
          />
        )}
      </Card>

      {/* Modal mejorado */}
      <Modal
        open={isVisibleModal}
        onCancel={() => setIsVisibleModal(false)}
        title={
          <div className="flex items-center">
            <BookOutlined className="mr-2 text-blue-500" />
            {modalTitle}
          </div>
        }
        footer={false}
        width={800}
        className="top-5"
      >
        {modalContent}
      </Modal>
    </div>
  );
}

export default TableUsers;
